/**
 * OffboardingDialog + HandoverPanel + GoodbyePanel — extracted from HrOnboarding.jsx (Iter 95 refactor).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { LogOut, Users, Sparkles, Mail, Check } from "lucide-react";
import { OnboardingTaskItem, AddCustomTaskButton, BulkAddTasksButton, ImportFromTemplateButton, SortableTaskList, BulkTaskToolbar, TaskListHeader } from "./OnboardingTaskItem";

function OffboardingDialog({ jid, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState({});
  const [handoverData, setHandoverData] = useState({ items: [], handover_kinds: [] });
  const [members, setMembers] = useState([]);      // for successor picker
  const [tab, setTab] = useState("tasks");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editingExitDate, setEditingExitDate] = useState(false);
  const [newExitDate, setNewExitDate] = useState("");
  const [selection, setSelection] = useState([]);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [saveTplName, setSaveTplName] = useState("");
  // Goodbye email state
  const [goodbye, setGoodbye] = useState(null);    // {enabled, subject, html, via, stats}
  const [goodbyeBusy, setGoodbyeBusy] = useState(false);
  const [sendConfirm, setSendConfirm] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/offboarding/journeys/${jid}`);
    setData(data);
    setNewExitDate(data.journey.exit_date || "");
  };
  const loadProgress = async () => {
    try { const r = await api.get(`/offboarding/journeys/${jid}/progress`); setProgress(r.data); }
    catch { /* progress optional */ }
  };
  const loadHandover = async () => {
    try {
      const r = await api.get(`/offboarding/journeys/${jid}/handover`);
      setHandoverData(r.data);
    } catch { /* empty is fine */ }
  };
  const loadMembers = async () => {
    if (!data?.journey?.workspace_id) return;
    try {
      const r = await api.get(`/workspaces/${data.journey.workspace_id}/members`);
      setMembers(r.data || []);
    } catch { setMembers([]); }
  };

  useEffect(() => { load(); loadProgress(); loadHandover(); }, [jid]);
  useEffect(() => { if (data) loadMembers(); }, [data?.journey?.workspace_id]);

  // Task-row mutations flow through OnboardingTaskItem — no local
  // patchTask helper needed. Kept the state hooks (`newExitDate`,
  // `handoverData`) intact since exit-date / handover / goodbye flows still
  // depend on them.
  const saveExitDate = async () => {
    if (!newExitDate) return;
    try {
      const r = await api.patch(`/offboarding/journeys/${jid}/exit-date`, { exit_date: newExitDate });
      toast.success(`Exit date updated — ${r.data.rescheduled} pending tasks rescheduled.`);
      setEditingExitDate(false);
      await load(); await loadProgress(); await onChange?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const convertAlumni = async () => {
    try {
      await api.post(`/employees/${data.employee.id}/convert-to-alumni`);
      toast.success("Converted to Alumni");
      setConfirmOpen(false);
      await onChange?.(); onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Convert failed")); }
  };
  const autoSeed = async () => {
    try {
      const r = await api.post(`/offboarding/journeys/${jid}/handover/auto-seed`);
      toast.success(r.data.seeded > 0 ? `Auto-seeded ${r.data.seeded} handover items.` : "No open work found to auto-seed.");
      setHandoverData((prev) => ({ ...prev, items: r.data.items }));
    } catch (e) { toast.error(apiErrorMessage(e, "Auto-seed failed")); }
  };
  const draftGoodbye = async () => {
    setGoodbyeBusy(true);
    try {
      const r = await api.get(`/offboarding/journeys/${jid}/goodbye/draft`);
      setGoodbye(r.data);
      if (!r.data.enabled) toast.info(r.data.hint || "Goodbye email is disabled in workspace settings.");
    } catch (e) { toast.error(apiErrorMessage(e, "Draft failed")); }
    finally { setGoodbyeBusy(false); }
  };
  const sendGoodbye = async () => {
    setGoodbyeBusy(true);
    try {
      await api.post(`/offboarding/journeys/${jid}/goodbye/send`, {
        subject: goodbye.subject, html: goodbye.html,
      });
      toast.success("Goodbye email sent 💌");
      setSendConfirm(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setGoodbyeBusy(false); }
  };

  if (!data) return null;
  const j = data.journey;
  const overallDone = Object.values(progress).reduce((a, b) => a + (b?.done || 0), 0);
  const overallTotal = Object.values(progress).reduce((a, b) => a + (b?.total || 0), 0);
  const overallPct = overallTotal ? Math.round((overallDone / overallTotal) * 100) : 0;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[95vw] sm:!max-w-[92vw] max-h-[92vh] overflow-auto" data-testid="off-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <LogOut className="w-4 h-4" /> {j.employee_name}
            <span className="text-xs font-normal text-muted-foreground ml-2">·</span>
            <span className="text-xs font-normal flex items-center gap-1">
              exit
              {editingExitDate ? (
                <>
                  <Input type="date" value={newExitDate} onChange={(e) => setNewExitDate(e.target.value)} className="h-7 w-36 rounded-none inline-block" data-testid="off-exit-date-input" />
                  <Button size="sm" className="rounded-none h-7" onClick={saveExitDate} data-testid="off-exit-date-save">Save</Button>
                  <Button size="sm" variant="ghost" className="rounded-none h-7" onClick={() => { setEditingExitDate(false); setNewExitDate(j.exit_date); }}>Cancel</Button>
                </>
              ) : (
                <>
                  <span className="font-mono font-semibold">{j.exit_date}</span>
                  <button className="text-primary hover:underline text-[11px]" onClick={() => setEditingExitDate(true)} data-testid="off-exit-date-edit">edit</button>
                </>
              )}
            </span>
            {j.goodbye_email_sent_at && (
              <span className="text-[10px] font-semibold px-2 py-0.5 bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 ml-auto">💌 Goodbye sent</span>
            )}
          </DialogTitle>
          <DialogDescription>Track handover, close the tasks, and (optionally) send a warm goodbye.</DialogDescription>
        </DialogHeader>

        {/* Overall progress + category chips */}
        <div className="border border-border p-3 bg-muted/30" data-testid="off-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-widest font-semibold">Overall progress</span>
            <span className="font-mono text-sm font-bold">{overallPct}% <span className="text-[11px] text-muted-foreground">({overallDone}/{overallTotal})</span></span>
          </div>
          <div className="h-2 bg-muted overflow-hidden mb-3">
            <div className="h-full bg-primary transition-all" style={{ width: `${overallPct}%` }} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(progress).map(([cat, b]) => (
              <div key={cat} className="flex items-center gap-2 text-xs">
                <span className="capitalize font-medium min-w-[80px]">{cat.replace("_", " ")}</span>
                <div className="flex-1 h-1.5 bg-muted overflow-hidden">
                  <div className={`h-full ${b.pct === 100 ? "bg-emerald-500" : "bg-primary"} transition-all`} style={{ width: `${b.pct}%` }} />
                </div>
                <span className="font-mono text-[10px] tabular-nums">{b.done}/{b.total}</span>
              </div>
            ))}
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-3">
          <TabsList className="rounded-none">
            <TabsTrigger value="tasks" className="rounded-none" data-testid="off-tab-tasks"><Check className="w-3.5 h-3.5 mr-1.5" /> Tasks</TabsTrigger>
            <TabsTrigger value="handover" className="rounded-none" data-testid="off-tab-handover"><Users className="w-3.5 h-3.5 mr-1.5" /> Handover <span className="ml-1 text-[9px] font-mono">({handoverData.items.length})</span></TabsTrigger>
            <TabsTrigger value="goodbye" className="rounded-none" data-testid="off-tab-goodbye"><Mail className="w-3.5 h-3.5 mr-1.5" /> Goodbye email</TabsTrigger>
          </TabsList>

          <TabsContent value="tasks" className="pt-3">
            {(() => {
              // Group by category so we can drop an "+ Add task" affordance
              // in each header — mirrors the onboarding side.
              const byCat = {};
              (data.tasks || []).forEach((t) => {
                const k = t.category || "other";
                byCat[k] = byCat[k] || [];
                byCat[k].push(t);
              });
              const cats = Object.keys(byCat);
              return cats.length === 0 ? (
                <div className="border-2 border-dashed border-border p-6 text-center text-xs text-muted-foreground">No tasks yet</div>
              ) : (
                <div className="space-y-3">
                  {cats.map((cat) => (
                    <div key={cat} className="border border-border">
                      <div className="px-3 py-2 border-b border-border bg-muted/40 label-eyebrow text-[10px] flex items-center justify-between">
                        <span>{cat.replace(/_/g, " ")} · {byCat[cat].length} tasks</span>
                        <div className="flex items-center gap-3">
                          <button
                            className="text-[10px] text-muted-foreground hover:text-primary"
                            onClick={() => {
                              const ids = byCat[cat].map((t) => t.id);
                              const allSel = ids.every((id) => selection.includes(id));
                              setSelection((s) => allSel
                                ? s.filter((id) => !ids.includes(id))
                                : Array.from(new Set([...s, ...ids])));
                            }}
                            data-testid={`off-sel-cat-${cat}`}
                          >
                            {byCat[cat].every((t) => selection.includes(t.id)) ? "Unselect all" : "Select all"}
                          </button>
                          <AddCustomTaskButton
                            jid={jid}
                            kind="offboarding"
                            category={cat}
                            members={members}
                            onCreated={async () => { await load(); await loadProgress(); await onChange?.(); }}
                          />
                          <BulkAddTasksButton
                            jid={jid}
                            kind="offboarding"
                            category={cat}
                            members={members}
                            onCreated={async () => { await load(); await loadProgress(); await onChange?.(); }}
                          />
                          <ImportFromTemplateButton
                            jid={jid}
                            wsId={j?.workspace_id}
                            kind="offboarding"
                            category={cat}
                            onCreated={async () => { await load(); await loadProgress(); await onChange?.(); }}
                          />
                        </div>
                      </div>
                      <TaskListHeader selectable />
                      <SortableTaskList
                        tasks={byCat[cat]}
                        kind="offboarding"
                        members={members}
                        selection={selection}
                        onToggleSelect={(id) => setSelection((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id])}
                        onChanged={async () => { await load(); await loadProgress(); await onChange?.(); }}
                        onReordered={async () => { await load(); }}
                      />
                    </div>
                  ))}
                  <BulkTaskToolbar
                    selection={selection}
                    kind="offboarding"
                    members={members}
                    onDone={async () => { setSelection([]); await load(); await loadProgress(); await onChange?.(); }}
                  />
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="handover" className="pt-3">
            <HandoverPanel
              jid={jid}
              data={handoverData}
              members={members}
              onReload={loadHandover}
              onProgressReload={loadProgress}
              onAutoSeed={autoSeed}
            />
          </TabsContent>

          <TabsContent value="goodbye" className="pt-3">
            <GoodbyePanel
              goodbye={goodbye}
              busy={goodbyeBusy}
              alreadySentAt={j.goodbye_email_sent_at}
              onDraft={draftGoodbye}
              onChange={(patch) => setGoodbye((g) => ({ ...(g || {}), ...patch }))}
              onSend={() => setSendConfirm(true)}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Close</Button>
          <Button variant="outline" className="rounded-none" onClick={() => { setSaveTplName(`${data.employee?.full_name || "Offboarding"} template`); setSaveTplOpen(true); }} data-testid="off-save-template">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> Save as template
          </Button>
          <Button className="rounded-none bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setConfirmOpen(true)} data-testid="off-convert-alumni">Convert to Alumni</Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
        <DialogContent className="z-[130] max-w-md" data-testid="off-save-tpl-dialog">
          <DialogHeader>
            <DialogTitle>Save offboarding as template</DialogTitle>
            <DialogDescription>Snapshot every task on this checklist into a reusable offboarding template.</DialogDescription>
          </DialogHeader>
          <Input value={saveTplName} onChange={(e) => setSaveTplName(e.target.value)} className="rounded-none h-9" placeholder="Template name" data-testid="off-save-tpl-name" maxLength={120} />
          <DialogFooter>
            <Button variant="ghost" className="rounded-none" onClick={() => setSaveTplOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={async () => {
              if (!saveTplName.trim()) return;
              try {
                const r = await api.post(`/onboarding/journeys/${jid}/save-as-template?is_offboarding=true`,
                  { name: saveTplName.trim(), scope: "workspace" });
                toast.success(`Template saved · ${(r.data.tasks || []).length} tasks`);
                setSaveTplOpen(false);
              } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
            }} disabled={!saveTplName.trim()} data-testid="off-save-tpl-save">Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="z-[130]" data-testid="convert-alumni-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Convert to Alumni?</AlertDialogTitle>
            <AlertDialogDescription>
              This deactivates the workspace login and moves the employee into the Alumni portal (letters + payslips access, per workspace settings).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="convert-alumni-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-amber-600 hover:bg-amber-700" onClick={convertAlumni} data-testid="convert-alumni-ok">Convert</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={sendConfirm} onOpenChange={setSendConfirm}>
        <AlertDialogContent className="z-[130]" data-testid="goodbye-send-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send goodbye email?</AlertDialogTitle>
            <AlertDialogDescription>
              This will email <b>{data.employee?.personal_email || data.employee?.work_email || "the employee"}</b> the message below. You cannot undo a send.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="goodbye-send-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-none" onClick={sendGoodbye} disabled={goodbyeBusy} data-testid="goodbye-send-ok">Send 💌</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

// ─────────────────────── Handover Panel ───────────────────────

function HandoverPanel({ jid, data, members, onReload, onProgressReload, onAutoSeed }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ kind: "task", title: "", description: "", successor_user_id: "" });
  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      await api.post(`/offboarding/journeys/${jid}/handover`, {
        kind: form.kind, title: form.title, description: form.description || null,
        successor_user_id: form.successor_user_id || null,
      });
      toast.success("Handover item added");
      setForm({ kind: "task", title: "", description: "", successor_user_id: "" });
      setShowAdd(false);
      await onReload(); await onProgressReload?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
  };
  const patch = async (id, patch) => {
    try {
      await api.patch(`/offboarding/handover/${id}`, patch);
      await onReload(); await onProgressReload?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const del = async (id) => {
    try { await api.delete(`/offboarding/handover/${id}`); await onReload(); await onProgressReload?.(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <div className="space-y-3" data-testid="handover-panel">
      <div className="flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">{data.items.length} items</span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={onAutoSeed} data-testid="handover-autoseed">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Auto-seed from open work
          </Button>
          <Button size="sm" className="rounded-none h-8" onClick={() => setShowAdd((v) => !v)} data-testid="handover-add-toggle">
            {showAdd ? "Cancel" : "+ Add item"}
          </Button>
        </div>
      </div>
      {showAdd && (
        <div className="border border-border p-3 space-y-2 bg-muted/30" data-testid="handover-add-form">
          <div className="grid grid-cols-2 gap-2">
            <select className="border border-border h-9 px-2 text-sm bg-background" value={form.kind}
                    onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="handover-kind">
              {(data.handover_kinds || []).map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <select className="border border-border h-9 px-2 text-sm bg-background" value={form.successor_user_id}
                    onChange={(e) => setForm({ ...form, successor_user_id: e.target.value })} data-testid="handover-successor">
              <option value="">— Successor (optional) —</option>
              {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
            </select>
          </div>
          <Input placeholder="Title (e.g. 'Client X redesign')" value={form.title}
                 onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="handover-title" />
          <Textarea rows={2} placeholder="Notes to guide the successor (optional)" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="handover-desc" />
          <Button size="sm" className="rounded-none" onClick={submit} data-testid="handover-save">Add</Button>
        </div>
      )}
      {data.items.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground border border-dashed border-border" data-testid="handover-empty">
          No handover items yet. Use <b>Auto-seed</b> to pull the employee&apos;s open projects &amp; tasks, or <b>+ Add item</b> manually.
        </div>
      ) : (
        <div className="divide-y divide-border border border-border">
          {data.items.map((h) => (
            <div key={h.id} className="px-3 py-2.5 grid grid-cols-1 sm:grid-cols-12 gap-2 items-start text-sm" data-testid={`handover-item-${h.id}`}>
              <div className="sm:col-span-1 flex items-center gap-2">
                <button
                  className={`w-4 h-4 border-2 flex items-center justify-center ${h.status === "completed" ? "bg-emerald-500 border-emerald-500 text-white" : "border-border hover:border-primary"}`}
                  onClick={() => patch(h.id, { status: h.status === "completed" ? "pending" : "completed" })}
                  data-testid={`handover-toggle-${h.id}`}>
                  {h.status === "completed" && <Check className="w-3 h-3" />}
                </button>
                <span className={`sm:hidden text-[10px] font-semibold px-1.5 py-0.5 border ${h.status === "completed" ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" : h.status === "assigned" ? "bg-blue-500/10 text-blue-700 border-blue-500/30" : "bg-muted text-muted-foreground border-border"}`}>{h.status}</span>
              </div>
              <div className="sm:col-span-5 min-w-0">
                <div className={h.status === "completed" ? "line-through text-muted-foreground" : "font-medium"}>
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mr-1.5">{h.kind}</span>
                  {h.title}
                </div>
                {h.description && <div className="text-[11px] text-muted-foreground mt-0.5">{h.description}</div>}
              </div>
              <div className="sm:col-span-4">
                <select className="w-full border border-border h-8 px-2 text-xs bg-background"
                        value={h.successor_user_id || ""} onChange={(e) => patch(h.id, { successor_user_id: e.target.value })}
                        data-testid={`handover-assign-${h.id}`}>
                  <option value="">— Assign successor —</option>
                  {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
                </select>
              </div>
              <div className="hidden sm:block sm:col-span-1 text-xs text-center">
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 border ${h.status === "completed" ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" : h.status === "assigned" ? "bg-blue-500/10 text-blue-700 border-blue-500/30" : "bg-muted text-muted-foreground border-border"}`}>{h.status}</span>
              </div>
              <div className="sm:col-span-1 text-right">
                <button className="text-[11px] text-red-500 hover:underline" onClick={() => del(h.id)} data-testid={`handover-delete-${h.id}`}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────── Goodbye Panel ───────────────────────

function GoodbyePanel({ goodbye, busy, alreadySentAt, onDraft, onChange, onSend }) {
  if (alreadySentAt) {
    return (
      <div className="border border-emerald-500/40 bg-emerald-500/5 p-4 text-sm" data-testid="goodbye-already-sent">
        <div className="font-semibold text-emerald-700">💌 Goodbye email already sent</div>
        <div className="text-xs text-muted-foreground mt-1">Sent on {new Date(alreadySentAt).toLocaleString()}.</div>
      </div>
    );
  }
  if (!goodbye) {
    return (
      <div className="space-y-3">
        <div className="border border-dashed border-border p-6 text-center" data-testid="goodbye-empty">
          <Mail className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
          <div className="text-sm font-semibold">Send a warm, personalised goodbye</div>
          <div className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            The AI drafts a message using their tenure, role, projects &amp; buddy vibe. You review, edit, and send. Purely optional — must be enabled in Settings.
          </div>
          <Button size="sm" className="rounded-none mt-4" onClick={onDraft} disabled={busy} data-testid="goodbye-draft">
            {busy ? "Drafting…" : "Draft with AI ✨"}
          </Button>
        </div>
      </div>
    );
  }
  if (!goodbye.enabled) {
    return (
      <div className="border border-amber-500/40 bg-amber-500/5 p-4 text-sm" data-testid="goodbye-disabled">
        <div className="font-semibold text-amber-700">Goodbye email is turned off</div>
        <div className="text-xs text-muted-foreground mt-1">{goodbye.hint}</div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="goodbye-editor">
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <Sparkles className="w-3 h-3" /> Drafted via <b>{goodbye.via === "ai" ? "AI (Claude)" : "template fallback"}</b>. Review and edit before sending.
        <Button size="sm" variant="ghost" className="rounded-none h-6 ml-auto" onClick={onDraft} disabled={busy} data-testid="goodbye-redraft">Re-draft</Button>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Subject</div>
        <Input value={goodbye.subject || ""} onChange={(e) => onChange({ subject: e.target.value })} data-testid="goodbye-subject" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Body (HTML)</div>
        <Textarea rows={9} value={goodbye.html || ""} onChange={(e) => onChange({ html: e.target.value })} data-testid="goodbye-html" className="font-mono text-xs" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Preview</div>
        <div className="border border-border p-3 bg-background max-h-64 overflow-auto text-sm" dangerouslySetInnerHTML={{ __html: goodbye.html || "" }} data-testid="goodbye-preview" />
      </div>
      <Button className="rounded-none" onClick={onSend} disabled={busy || !goodbye.subject || !goodbye.html} data-testid="goodbye-send-open">
        Send 💌
      </Button>
    </div>
  );
}

export { OffboardingDialog, HandoverPanel, GoodbyePanel };
